import classnames from 'classnames/bind'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { STORYBLOK_COMPONENTS } from '~/lib/storyblok-types'

import ProductPins from '~/components/Abstracts/ProductsPins'
import ImageWithLabel, { IMAGE_SIZE } from '~/components/Slices/ImageWithLabel'
import { RightColumnContents } from '~/components/Slices/TwoColumnsLayoutLine'
import AccordionItems from '~/components/UI/AccordionItems'
import CustomTable from '~/components/UI/CustomTable'
import Quote from '~/components/UI/Quote'
import TwoColumnsAccordion from '~/components/UI/TwoColumnsAccordion'
import Wysiwyg from '~/components/UI/Wysiwyg'

import { useStyle } from '~/providers/StyleProvider'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type RowProps = {
  content?: RightColumnContents[]
  isSingleItemHalfWidth?: boolean
}

export type ColumnRowProps = RowProps & {
  className?: string
}

function ColumnRow({
  className,
  content,
  isSingleItemHalfWidth,
}: ColumnRowProps) {
  const bodyStyle = useStyle({
    textPreset: GlobalTextPreset.Text14GroteskRegular,
    color: GlobalThemeColors.Gray,
  })
  const headlineStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskMedium,
    color: GlobalThemeColors.Black,
    textStyling: GlobalTextStyling.UpperCase,
  })

  return (
    <div
      className={cx(
        css.RightColumnRow,
        className,
        css?.[`layout-${content?.length}-items`],
        {
          singleHalfWidth: isSingleItemHalfWidth,
        },
      )}>
      {content?.length > 0 &&
        content?.map(({ type, ...props }, i) => {
          switch (type) {
            case STORYBLOK_COMPONENTS.QUOTE:
              return (
                <Quote
                  className={cx(css.item, css.Quote)}
                  key={`right_column_item_${i}`}
                  {...props}
                />
              )
            case 'two-columns-accordion':
              return (
                <TwoColumnsAccordion
                  className={cx(css.item, css?.TwoColumnsAccordion)}
                  key={`right_column_item_${i}`}
                  headlineClassName={headlineStyle}
                  bordersTheme={GlobalThemeColors.SilverChalice}
                  {...props}
                />
              )
            case STORYBLOK_COMPONENTS.IMAGE_WITH_LABEL:
              return (
                <ImageWithLabel
                  className={cx(css.item, css.ImageWithLabel)}
                  layout={
                    content?.length === 2 ? IMAGE_SIZE.SMALL : IMAGE_SIZE.LARGE
                  }
                  key={`right_column_item_${i}`}
                  {...props}
                />
              )
            case STORYBLOK_COMPONENTS.WYSIWYG:
              return (
                <Wysiwyg
                  className={cx(css.item, css.Wysiwyg)}
                  key={`right_column_item_${i}`}
                  {...props}
                />
              )
            case STORYBLOK_COMPONENTS.TEXT_ACCORDION:
              return (
                <AccordionItems
                  className={css.item}
                  key={`right_column_item_${i}`}
                  bodyClassName={bodyStyle}
                  headlineClassName={headlineStyle}
                  bordersTheme={GlobalThemeColors.SilverChalice}
                  {...props}
                />
              )
            case STORYBLOK_COMPONENTS.TABLE:
              return (
                <CustomTable
                  key={`right_column_item_${i}`}
                  className={cx(css.item, css.Table)}
                  {...props}
                />
              )
            case STORYBLOK_COMPONENTS.IMAGE_WITH_PINS:
              return (
                <ProductPins
                  className={cx(css.item, css.ImageWithLabel)}
                  key={`right_column_item_${i}`}
                  {...props}
                  layout={
                    content?.length === 2 ? IMAGE_SIZE.SMALL : IMAGE_SIZE.LARGE
                  }
                />
              )
            default:
              return null
          }
        })}
    </div>
  )
}

export default ColumnRow
